import { useEffect, useState } from "react";
import DataTable from "react-data-table-component";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Card, CardBody, Col, Row } from "reactstrap";

import { H3, ToolTip } from "../../AbstractElements";
import {
  ALL_COURSE_FILTER_STATUS,
  courseTypeMap,
  requestTypeMap,
  tmTabsStatusMap,
} from "../../Api/constants";
import FixedFooter from "../../CommonElements/FixedFooter";
import SearchBar from "../../CommonElements/SearchBar";
import TextTooltip from "../../CommonElements/TextTooltip";
import ViewMoreDropdown from "../../CommonElements/ViewMoreDropdown";
import CourseCard from "../../container/CourseCard";
import DynamicFilterPanel from "../../container/DynamicFilterPanel";
import NoData from "../../container/NoData";
import DefaultPagination from "../../container/Paginator";
import { useAppDispatch, useAppSelector } from "../../ReduxToolkit/Hooks";
import { getCourseList } from "../../ReduxToolkit/Reducers/CourseSlice";
import { getJobRoleList } from "../../ReduxToolkit/Reducers/ManageMasterDataSlice";
import {
  getCourseTagsList,
  getDomainList,
} from "../../ReduxToolkit/Reducers/MasterSlice";
import {
  CourseName,
  CourseType,
  Domain,
  Duration,
  SideBarTitleCourseCatalog,
  External,
  InternalHeader,
  NotYetAdded,
  NoDataText,
  Industry,
  Tags,
  CourseID,
} from "../../utils/Constant";
import {
  CARD_PER_PAGE,
  courseFilterDropdownList,
  createTooltipCell,
  filterNullFromArray,
  formatCustomDate,
  formatMinutes,
  truncateText,
} from "../../utils/helper/helper";
import useIsMobile from "../../utils/helper/responsive";

import "./style.scss";

const CourseCatalog = () => {
  const isMobile = useIsMobile();
  const [searchResults, setSearchResults] = useState([]);
  const [domainListData, setDomainListData] = useState([]);
  const [clearData, setClearData] = useState(false);
  const [viewMode, setViewMode] = useState("card");
  const [transformedData, setTransformedData] = useState({});
  const [noDataFound, setNoDataFound] = useState(false);
  const [tagListData, setTagListData] = useState([]);

  const { courseList } = useAppSelector(state => state.course);
  const { domainList, courseTags } = useAppSelector(state => state.master);
  const { jobRoleList } = useAppSelector(state => state.manageMasterData);
  const [filters, setFilters] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return {
      domain: params.get("domain")?.split(",") || [],
      request_type: params.get("request_type")?.split(",") || [],
      tags: params.get("tags")?.split(",") || [],
    };
  });

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const viewParam = params.get("view");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = CARD_PER_PAGE;

  const filtersConfig = [
    {
      key: "domain",
      label: Domain,
      type: "searchable-checkbox",
      searchable: true,
      collapsible: true,
      options: domainListData,
    },
    {
      key: "request_type",
      label: CourseType,
      type: "checkbox",
      collapsible: true,
      options: courseFilterDropdownList.map(({ name, label }) => ({
        label,
        value: name,
      })),
    },
    {
      key: "tags",
      label: Tags,
      type: "checkbox",
      collapsible: true,
      options: tagListData,
    },
  ];

  useEffect(() => {
    setSearchResults(courseList?.course_list);
  }, [courseList?.course_list]);

  useEffect(() => {
    dispatch(getJobRoleList());
    dispatch(getDomainList());
    dispatch(getCourseTagsList());
  }, []);

  useEffect(() => {
    const tags = [];
    courseTags?.tags?.forEach(element => {
      tags.push({
        value: element.id.toString(),
        name: element.name,
        label: element.name,
      });
    });
    setTagListData(tags);
  }, [courseTags]);

  useEffect(() => {
    if (jobRoleList?.job_role && Array.isArray(jobRoleList?.job_role)) {
      const result = jobRoleList.job_role.reduce((acc, item) => {
        acc[item.id] = {
          name: item.name,
          industry_name: item.industry_name,
        };
        return acc;
      }, {});
      setTransformedData(result);
    }
  }, [jobRoleList]);

  useEffect(() => {
    if (viewParam) {
      setViewMode(viewParam);
    }
  }, [viewParam]);

  useEffect(() => {
    fetchCourses();
  }, []);

  useEffect(() => {
    const domains = [];
    domainList?.domain?.forEach(element => {
      domains.push({
        value: element.id.toString(),
        name: element.domain_name,
        label: element.domain_name,
      });
    });
    setDomainListData(domains);
  }, [domainList]);

  const fetchCourses = () => {
    const filteredCourseType =
      filters["request_type"]?.length > 0
        ? filters["request_type"]
        : ALL_COURSE_FILTER_STATUS;
    const filteredDomain =
      filters["domain"]?.length > 0 ? filters["domain"] : null;

    const filtereTags = filters["tags"]?.length > 0 ? filters["tags"] : null;

    dispatch(
      getCourseList({
        status: tmTabsStatusMap["published"],
        course_type: filteredCourseType,
        tags: filtereTags,
        domain: filteredDomain,
      })
    ).then(response => {
      setNoDataFound(!response?.payload?.course_list?.length);
    });
  };

  const handleSearch = query => {
    setClearData(false);
    setCurrentPage(1);
    const filteredCourse = courseList?.course_list?.filter(course =>
      course.course_name.toLowerCase().includes(query.toLowerCase())
    );
    setSearchResults(!query ? courseList?.course_list : filteredCourse);
    setNoDataFound(!filteredCourse || filteredCourse?.length === 0);
  };

  const handlePageChange = pageNumber => {
    setCurrentPage(pageNumber);
  };

  const handleClear = () => {
    setSearchResults([]);
    setClearData(true);
    setFilters({});
    setCurrentPage(1);
    dispatch(
      getCourseList({
        status: tmTabsStatusMap["published"],
        course_type: [],
        domain: [],
        page: "course_management",
      })
    ).then(response => {
      setNoDataFound(!response?.payload?.course_list?.length);
    });
    navigate(`${location.pathname}?${params.toString()}`);
  };

  const handleClick = item => {
    return navigate(`/course-catalog/course-details?course_id=${item.id}`);
  };

  const columns = [
    {
      name: CourseName,
      selector: row => row?.course_name,
      cell: row => (
        <>
          <span
            id={`tooltip-name-${row.id}`}
            className="text-truncate d-flex align-items-center"
          >
            {truncateText(row?.course_name, 40)}
          </span>
          <ToolTip placement="top" target={`tooltip-name-${row.id}`}>
            {row?.course_name}
          </ToolTip>
        </>
      ),
      minWidth: "19rem",
      sortable: true,
    },
    {
      name: CourseType,
      selector: row => courseTypeMap[row.course_type],
      minWidth: "5rem",
      sortable: true,
    },
    {
      name: Domain,
      selector: row =>
        row?.domain_names?.filter(domain => domain !== null).length > 0
          ? row.domain_names
          : "-",
      minWidth: "5rem",
      sortable: true,
    },
    {
      name: Industry,
      sortable: true,
      cell: row => {
        const result = extractFields(row?.intended_audience);
        return createTooltipCell(
          `${result.industry_name.length > 0 ? filterNullFromArray(result?.industry_name)?.join(", ") : "-"}`,
          `industry-${row.id}`
        );
      },
      minWidth: "7rem",
    },
    {
      name: Tags,
      selector: row =>
        filterNullFromArray(row?.tags)?.length > 0 ? row?.tags.join(", ") : "-",
      cell: row =>
        createTooltipCell(
          filterNullFromArray(row?.tags)?.join(", ") || "-",
          `tags-${row.id}`
        ),
      sortable: true,
      minWidth: "7rem",
    },
    {
      name: CourseID,
      selector: row => row.course_id,
      sortable: true,
      minWidth: "7rem",
    },

    {
      name: Duration,
      selector: row => {
        return formatMinutes(row?.duration);
      },
      cell: row => {
        return formatMinutes(row?.duration);
      },
      minWidth: "5rem",
      sortable: true,
    },
  ];

  const handleSetView = type => {
    setViewMode(type);
    params.set("view", type);
    navigate(`${location.pathname}?${params.toString()}`);
  };

  const extractFields = ids => ({
    name: ids.map(id => transformedData[id]?.name || null),
    industry_name: ids.map(id => transformedData[id]?.industry_name || null),
  });

  const renderCourseCard = course => {
    const result = extractFields(course.intended_audience);
    return (
      <div>
        <Link to={`/course-catalog/course-details?course_id=${course.id}`}>
          <CourseCard
            levels={
              course?.expertise_levels
                ? course?.expertise_levels.filter(item => item !== null)
                : []
            }
            code={course.course_id}
            title={course?.course_name}
            domain={
              course?.domain_names
                ? course?.domain_names.filter(item => item !== null)
                : []
            }
            tags={course.tags.length > 0 ? course.tags : ["-"]}
            info={{
              audience: result.industry_name,
              technician: result.name,
              date: formatCustomDate(course?.created_at),
              duration: formatMinutes(course?.duration),
            }}
            courseType={course?.course_type}
            courseId={course?.id?.toString()}
            image={course.thumbnail_url}
          />
        </Link>
      </div>
    );
  };

  const renderCoursesContent = () => {
    if (noDataFound) {
      return (
        <NoData svg={"empty-folder-icon"} title={NoDataText} showCard={false} />
      );
    }

    if (courseList?.course_list?.length === 0) {
      return (
        <NoData
          svg={"empty-folder-icon"}
          title={NotYetAdded}
          showCard={false}
        />
      );
    }

    const cardData =
      searchResults?.length > 0 ? searchResults : courseList?.course_list;

    return viewMode === "card" ? (
      <div className="course-list__card">
        {cardData
          ?.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
          .map(course => renderCourseCard(course))}
      </div>
    ) : (
      renderRequestTable()
    );
  };

  const renderRequestTable = () => {
    return (
      <Col>
        <Card className="p-1">
          <CardBody>
            <DataTable
              highlightOnHover
              onRowClicked={handleClick}
              columns={columns}
              data={searchResults}
              pagination
              fixedHeader
              striped
            />
          </CardBody>
        </Card>
      </Col>
    );
  };

  const handleApply = () => {
    setClearData(true);
    setSearchResults([]);
    fetchCourses();
    setCurrentPage(1);
    Object.keys(filters).forEach(key => {
      const value = filters[key];
      if (Array.isArray(value) ? value.length > 0 : value) {
        params.set(key, Array.isArray(value) ? value.join(",") : value);
      } else {
        params.delete(key);
      }
    });

    navigate(`${location.pathname}?${params.toString()}`);
  };

  return (
    <div className="page-body page-body-margin course-catelog-list">
      <Card className="p-1">
        <CardBody>
          <div
            className={`${!isMobile && "d-flex justify-content-between"}  align-items-center mt-sm-0 mt-2 gap-3 ms-auto`}
          >
            <H3>{SideBarTitleCourseCatalog}</H3>
            <div className="d-flex gap-2 align-items-center">
              <SearchBar
                showSearch
                className="mb-1"
                onSearch={handleSearch}
                clearOnProp={clearData}
              />
              <ViewMoreDropdown
                onChange={handleSetView}
                currentView={viewMode}
              />
              <div>
                <div>
                  <DynamicFilterPanel
                    config={filtersConfig}
                    values={filters}
                    onChange={setFilters}
                    onClear={handleClear}
                    onApply={() => handleApply()}
                  />
                </div>
              </div>
            </div>
          </div>
        </CardBody>
      </Card>

      <Row className="pb-5">{renderCoursesContent()}</Row>

      {viewMode === "card" &&
        !noDataFound &&
        (searchResults?.length > 0 ||
          (searchResults?.length === 0 &&
            courseList?.course_list?.length > 0)) && (
          <FixedFooter>
            <DefaultPagination
              currentPage={currentPage}
              totalPages={Math.ceil(
                (searchResults?.length > 0
                  ? searchResults?.length
                  : courseList?.course_list?.length) / itemsPerPage
              )}
              onPageChange={handlePageChange}
            />
          </FixedFooter>
        )}
    </div>
  );
};

export default CourseCatalog;
